import React, { useMemo, useRef, useState } from "react";
import classNames from "classnames";

import FormInput from "../../Inputs/FormInput";

import styles from "./imageManagementUploadForm.module.css";

const buildPreviewUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const normalizedPath = value.replace(/\\/g, "/");
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

  const withUploads = normalizedPath.startsWith("uploads/")
    ? normalizedPath
    : normalizedPath.startsWith("/uploads/")
      ? normalizedPath.slice(1)
      : `uploads/${normalizedPath}`;

  return `${process.env.REACT_APP_SERVER_URL}/${withUploads}`;
};

const extractFileName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("/").pop() || value;
  if (value.name) return value.name;
  return "";
};

const ImageManagementUploadForm = ({
  formFields,
  formData,
  errors,
  readOnly,
  getFieldLabel,
  handleChange,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const nameField =
    formFields.find((field) => field.name === "product_name") ||
    formFields.find((field) => field.type !== "file");
  const imageField =
    formFields.find((field) => field.name === "image_file") ||
    formFields.find((field) => field.type === "file");

  const imageValue = imageField ? formData[imageField.name] : null;
  const fileName = extractFileName(imageValue);
  const previewUrl = useMemo(() => {
    if (typeof imageValue === "string") return buildPreviewUrl(imageValue);
    if (imageValue && typeof imageValue === "object" && imageValue.name) {
      return URL.createObjectURL(imageValue);
    }
    return "";
  }, [imageValue]);

  const openFileDialog = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (file) => {
    if (!imageField || readOnly) return;
    handleChange(imageField.name, file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (readOnly) return;
    setIsDragging(false);

    const [file] = event.dataTransfer.files || [];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!readOnly) setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  return (
    <div className={styles.formWrap}>
      {nameField && (
        <FormInput
          label={getFieldLabel(nameField)}
          name={nameField.name}
          type={nameField.type || "text"}
          error={errors[nameField.name]}
          value={formData[nameField.name] || ""}
          disabled={readOnly || nameField.disabled}
          onChange={(e) => handleChange(nameField.name, e.target.value)}
        />
      )}

      {imageField && (
        <div className={styles.uploadBlock}>
          <label className={styles.uploadLabel}>
            {getFieldLabel(imageField)}
          </label>

          <div
            className={classNames(styles.uploadCard, {
              [styles.uploadCardActive]: isDragging,
              [styles.uploadCardDisabled]: readOnly,
              [styles.uploadCardError]: Boolean(errors[imageField.name]),
            })}
            role="button"
            tabIndex={0}
            onClick={openFileDialog}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") openFileDialog();
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className={styles.uploadIcon}>
              <i className="bi bi-cloud-arrow-up" />
            </div>
            <div className={styles.uploadTitle}>Browse image (jpg, png)</div>
            <div className={styles.uploadHint}>or drag and drop here</div>

            {fileName && (
              <div className={styles.fileName}>
                Selected: {fileName}
              </div>
            )}
          </div>

          {previewUrl && (
            <div className={styles.previewRow}>
              <img className={styles.previewImage} src={previewUrl} alt="Preview" />
            </div>
          )}

          {errors[imageField.name] && (
            <div className={styles.errorText}>{errors[imageField.name]}</div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            className={styles.hiddenFileInput}
            disabled={readOnly}
          />
        </div>
      )}
    </div>
  );
};

export default ImageManagementUploadForm;
